import { useQuery } from '@tanstack/react-query'
import { useState, useCallback, useMemo } from 'react'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import {
  getDrugs,
  getAdvancedDrugs,
  getDrug,
} from '@/features/internal/drugs/apis/soapTemplate'
import {
  Drug,
  DrugDetail,
  PaginatedResponse,
} from '@/features/internal/drugs/types/soapTemplate'
import { AdvancedSearchCondition } from '@/shared/types/advancedSearch'
import { ADVANCED_SEARCH } from '@/shared/constants/const'

interface UseDrugSearchQueryOptions {
  /**
   * 初期検索キーワード
   */
  initialSearchKeyword?: string

  /**
   * ページサイズ
   */
  pageSize?: number

  /**
   * クエリを有効にするかどうか
   */
  enabled?: boolean
}

interface UseDrugSearchQueryReturn {
  // 通常検索の状態
  normalSearchKeyword: string
  setNormalSearchKeyword: (keyword: string) => void

  // 高度検索の状態
  advancedConditions: AdvancedSearchCondition[]
  setAdvancedConditions: (conditions: AdvancedSearchCondition[]) => void

  // 検索結果（標準 TanStack Query API）
  searchResults: PaginatedResponse<Drug> | null
  isLoading: boolean
  error: Error | null

  // 検索実行（命令的インターフェース）
  executeNormalSearch: (keyword?: string, page?: number) => void
  executeAdvancedSearch: (
    conditions: AdvancedSearchCondition[],
    page?: number
  ) => void

  // ページネーション
  currentPage: number
  setCurrentPage: (page: number) => void

  // 検索モード
  isAdvancedMode: boolean
  setIsAdvancedMode: (mode: boolean) => void

  // リセット
  resetSearch: () => void
}

/**
 * 医薬品検索クエリフック（TanStack Query）
 *
 * 責務:
 * - 通常検索・高度検索のデータ取得
 * - 検索状態の管理
 * - キャッシュ管理
 *
 * 使用例:
 * ```tsx
 * const {
 *   normalSearchKeyword,
 *   setNormalSearchKeyword,
 *   executeNormalSearch,
 *   searchResults,
 *   loading,
 * } = useDrugSearchQuery()
 *
 * // 検索実行
 * executeNormalSearch('カルボ')
 * ```
 */
export const useDrugSearchQuery = (
  options: UseDrugSearchQueryOptions = {}
): UseDrugSearchQueryReturn => {
  const {
    initialSearchKeyword = '',
    pageSize = ADVANCED_SEARCH.DEFAULT_PAGE_SIZE,
    enabled = true,
  } = options

  // 検索状態
  const [normalSearchKeyword, setNormalSearchKeyword] =
    useState(initialSearchKeyword)
  const [advancedConditions, setAdvancedConditions] = useState<
    AdvancedSearchCondition[]
  >([])
  const [currentPage, setCurrentPage] = useState(1)
  const [isAdvancedMode, setIsAdvancedMode] = useState(false)

  // 実行済み検索パラメータ（クエリキー用）
  const [executedSearch, setExecutedSearch] = useState<{
    keyword: string
    conditions: AdvancedSearchCondition[]
    page: number
    isAdvanced: boolean
  } | null>(null)

  // クエリパラメータ（メモ化）
  const queryParams = useMemo(() => {
    if (!executedSearch) return null
    return {
      keyword: executedSearch.keyword,
      conditions: executedSearch.conditions,
      page: executedSearch.page,
      isAdvancedMode: executedSearch.isAdvanced,
    }
  }, [executedSearch])

  // TanStack Query
  // pageSize と executedSearch を queryKey に含める（@tanstack/query/exhaustive-deps 対応）
  const query = useQuery({
    queryKey: [
      ...queryKeys.drugs.search(
        queryParams ?? {
          keyword: '',
          conditions: [],
          page: 1,
          isAdvancedMode: false,
        }
      ),
      { pageSize, executedSearch },
    ],
    queryFn: async (): Promise<PaginatedResponse<Drug>> => {
      const emptyResponse: PaginatedResponse<Drug> = {
        count: 0,
        results: [],
        page_size: pageSize,
        next: null,
        previous: null,
      }

      if (!executedSearch) {
        return emptyResponse
      }

      if (executedSearch.isAdvanced) {
        // 高度検索
        const validConditions = executedSearch.conditions.filter((c) =>
          c.value.trim()
        )
        if (validConditions.length === 0) {
          return emptyResponse
        }
        const response = await getAdvancedDrugs(
          validConditions,
          executedSearch.page,
          pageSize
        )
        return response.data
      } else {
        // 通常検索
        if (!executedSearch.keyword.trim()) {
          return emptyResponse
        }
        const response = await getDrugs(
          executedSearch.page,
          executedSearch.keyword
        )
        return response.data
      }
    },
    enabled: enabled && !!executedSearch,
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
  })

  // 通常検索の実行（命令的インターフェース）
  const executeNormalSearch = useCallback(
    (keyword?: string, page = 1) => {
      const searchKeyword = keyword ?? normalSearchKeyword
      if (!searchKeyword.trim()) {
        setExecutedSearch(null)
        return
      }

      // 検索キーワードを状態に反映
      if (searchKeyword !== normalSearchKeyword) {
        setNormalSearchKeyword(searchKeyword)
      }

      setCurrentPage(page)
      setExecutedSearch({
        keyword: searchKeyword,
        conditions: [],
        page,
        isAdvanced: false,
      })
    },
    [normalSearchKeyword]
  )

  // 高度検索の実行（命令的インターフェース）
  const executeAdvancedSearch = useCallback(
    (conditions: AdvancedSearchCondition[], page = 1) => {
      const validConditions = conditions.filter((c) => c.value.trim())
      if (validConditions.length === 0) {
        setExecutedSearch(null)
        return
      }

      // 検索条件を状態に保持
      setAdvancedConditions(conditions)
      setCurrentPage(page)
      setExecutedSearch({
        keyword: '',
        conditions,
        page,
        isAdvanced: true,
      })
    },
    []
  )

  // 検索リセット
  const resetSearch = useCallback(() => {
    setNormalSearchKeyword('')
    setAdvancedConditions([])
    setCurrentPage(1)
    setIsAdvancedMode(false)
    setExecutedSearch(null)
  }, [])

  return {
    // 通常検索の状態
    normalSearchKeyword,
    setNormalSearchKeyword,

    // 高度検索の状態
    advancedConditions,
    setAdvancedConditions,

    // 検索結果（標準 TanStack Query API）
    searchResults: query.data ?? null,
    isLoading: query.isLoading || query.isFetching,
    error: query.error ?? null,

    // 検索実行
    executeNormalSearch,
    executeAdvancedSearch,

    // ページネーション
    currentPage,
    setCurrentPage,

    // 検索モード
    isAdvancedMode,
    setIsAdvancedMode,

    // リセット
    resetSearch,
  }
}

/**
 * 医薬品詳細取得クエリ
 *
 * 責務:
 * - 医薬品詳細データの取得（TanStack Query）
 *
 * 使用例:
 * ```tsx
 * const { data: drug, isLoading, error } = useDrugDetailQuery(drugId)
 * ```
 */
export const useDrugDetailQuery = (drugId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.drugs.detail(drugId ?? ''),
    queryFn: async (): Promise<DrugDetail> => {
      if (!drugId) {
        throw new Error('Drug ID is required')
      }
      const response = await getDrug(drugId)
      return response.data
    },
    enabled: !!drugId,
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
  })
}
